import React, { useMemo } from 'react';
import { useCompetitorProfiles } from '../services/api';
import { competitorEntries, matchCompetitors } from '../utils/aiAnalysisDisplay';
import { lastReportSummary } from '../utils/earningsElapsed';
import { finiteOrNull } from '../utils/numbers';
import { formatMarketCap, formatSignedPercent } from './utils';
import { columnTooltip } from '../config/columnConfig';
import { METRIC_DESCRIPTIONS } from '../constants/metricDescriptions';
import { signColor, TEXT_MUTED } from '../constants/uiColors';
import { SectionHeading } from './AiAnalysisSections';

// The AI analysis's Competition section as a comparison table (issue #445): the
// subject stock first, then each AI-named competitor with the same basic data,
// and the AI's one-line differentiation under each competitor. No live price —
// only sizes, fundamentals, valuation and settled-ish moves.
//
// A competitor gets data ONLY when its ticker resolves and our stored name
// names the company the AI wrote (matchCompetitors); otherwise the row keeps
// the name and the prose, as the section always showed.

const DASH = <span style={{ color: TEXT_MUTED }}>—</span>;

// Signed, sign-coloured percent; `scale` 100 for a fraction (0.256 -> +25.6%).
const signed = (value, scale = 1) => {
  const number = finiteOrNull(value);
  if (number === null) return DASH;
  return <span style={{ color: signColor(number) }}>{formatSignedPercent(number * scale, 1)}</span>;
};

const unsignedFraction = (value) => {
  const number = finiteOrNull(value);
  return number === null ? DASH : `${(number * 100).toFixed(1)}%`;
};

const ratio = (value, notMeaningful) => {
  const number = finiteOrNull(value);
  if (number === null) return DASH;
  if (number <= 0) return <span style={{ color: TEXT_MUTED }} title={notMeaningful}>n/m</span>;
  return number.toFixed(1);
};

const SinceEarnings = ({ stock }) => {
  const { since, elapsed } = lastReportSummary({
    reactionDate: stock.earningsReactionDate,
    sincePercent: stock.stockChange,
  });
  if (since === null) return DASH;
  return (
    <>
      {signed(since)}
      {elapsed && <span className="ai-comp-elapsed">{elapsed}</span>}
    </>
  );
};

// Column order, header, header hover and cell renderer. Hovers reuse the stock
// table's own column tooltips where the field is a column.
const COLUMNS = [
  { label: 'Mkt cap', title: columnTooltip('marketCap'),
    cell: (s) => (finiteOrNull(s.marketCap) === null ? DASH : formatMarketCap(s.marketCap)) },
  { label: 'Rev growth', title: columnTooltip('growth'), cell: (s) => signed(s.revenueGrowth, 100) },
  { label: 'Gross margin', title: METRIC_DESCRIPTIONS.grossMargins, cell: (s) => unsignedFraction(s.grossMargins) },
  { label: 'Fwd P/E', title: METRIC_DESCRIPTIONS.forwardPE,
    cell: (s) => ratio(s.forwardPE, 'Not meaningful: earnings expected to be negative') },
  { label: 'P/S', title: METRIC_DESCRIPTIONS.priceToSales,
    cell: (s) => ratio(s.priceToSalesTrailing12Months, 'Not meaningful') },
  { label: '3M', title: columnTooltip('priceChange3m'), cell: (s) => signed(s.priceChange3m) },
  { label: 'Since earnings', title: columnTooltip('stockChange'), cell: (s) => <SinceEarnings stock={s} /> },
];

const Cells = ({ stock }) => COLUMNS.map((column) => <td key={column.label}>{column.cell(stock)}</td>);

const CompetitionSection = ({ stock, competitors }) => {
  const entries = useMemo(() => competitorEntries(competitors, stock.tickerSymbol), [competitors, stock.tickerSymbol]);
  const tickers = useMemo(() => entries.map((entry) => entry.ticker).filter(Boolean), [entries]);
  const { data: profiles, isLoading } = useCompetitorProfiles(tickers);
  const rows = useMemo(() => matchCompetitors(entries, profiles), [entries, profiles]);

  if (entries.length === 0) return null;

  return (
    <section className="bg-white rounded-lg shadow p-6 mb-4">
      <SectionHeading>Competition</SectionHeading>
      <div className="ai-comp-scroll">
        <table className="ai-comp-table">
          <thead>
            <tr>
              <th>Company</th>
              {COLUMNS.map((column) => <th key={column.label} title={column.title}>{column.label}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr className="ai-comp-row ai-comp-row--subject">
              <td>
                <span className="ai-comp-name">{stock.name}</span>
                <span className="ai-comp-ticker">{stock.tickerSymbol}</span>
                <span className="ai-comp-tag">this stock</span>
              </td>
              <Cells stock={stock} />
            </tr>

            {rows.map((row) => (
              <React.Fragment key={row.ticker || row.name}>
                <tr className="ai-comp-row">
                  <td>
                    <span className="ai-comp-name">{row.name}</span>
                    {row.stock && (
                      <a
                        className="ai-comp-ticker ai-comp-ticker--link"
                        href={`/stock/${encodeURIComponent(row.stock.tickerSymbol)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${row.stock.tickerSymbol} in a new tab`}
                      >
                        {row.stock.tickerSymbol}
                      </a>
                    )}
                  </td>
                  {row.stock
                    ? <Cells stock={row.stock} />
                    : (
                      <td
                        colSpan={COLUMNS.length}
                        className="ai-comp-nodata"
                        title="Not matched to a stock we track (private, foreign-listed, delisted, or a ticker that names another company)"
                      >
                        {isLoading && row.ticker ? 'Loading…' : 'No market data'}
                      </td>
                    )}
                </tr>
                {row.differentiation && (
                  <tr className="ai-comp-diff">
                    <td colSpan={COLUMNS.length + 1}><div>{row.differentiation}</div></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default CompetitionSection;
